import { stringify } from "querystring";

import {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";

import { useRouter } from "next/router";

import { useSearchParamsControls } from "@/search/NewSearchPageMVP/provider/hooks";
import { searchPageV2ContextInitialValue } from "@/search/NewSearchPageMVP/provider/SearchPageProvider.constants";
import {
  getValidQueryParams,
  initializeSearchParams,
} from "@/search/NewSearchPageMVP/provider/SearchPageProvider.utils";
import {
  useFetchAgathaSchema,
  useFetchSearchResults,
  useTaxonomyTagsFetch,
} from "@/search/NewSearchPageMVP/provider/services";
import { IAgathaSearchParams } from "@/search/NewSearchPageMVP/types/IAgathaSearchParams";
import { AgathaSchemaParamsEnum } from "@/search/NewSearchPageMVP/types/IParametersSchema";
import { IReadingSkill } from "@/search/NewSearchPageMVP/types/IReadingSkill";
import { ISearchResultsResponse } from "@/search/NewSearchPageMVP/types/ISearchResultsResponse";
import useLazyReadingSkillsQuery from "@/search/useLazyReadingSkillsQuery";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";
import { ISearchResult } from "../types/ISearchResult";
import {
  ITaxonomyApiResponse,
  ITaxonomyApiResponseContent,
  ITaxonomyTags,
  TAXONOMY_DEPTH,
} from "../FilterPanel/filters/Topics/TaxonomyTags";

export const SearchPageContext = createContext<ISearchPageContext>(
  searchPageV2ContextInitialValue,
);
export const useSearchPageContext = () => useContext(SearchPageContext);

const SearchPageProvider = ({ children }: ISearchPageProvider) => {
  const { query, push } = useRouter();
  const { data: user } = useUser();
  const { canModifyContentSettings } = getUserProperties(user, {
    shouldUseNewUserApi: false,
  });

  const [sendAdminContentSettings, setSendAdminContentSettings] =
    useState<boolean>(false);

  const [searchId, setSearchId] = useState("");
  const [searchResults, setSearchResults] = useState<ISearchResult[]>([]);
  const [searchParams, setSearchParams] = useState(
    initializeSearchParams(query),
  );
  const [debouncedParams, setDebouncedParams] = useState(
    searchParams.toString(),
  );

  const [taxonomyTagsSlugs, setTaxonomyTagsSlugs] = useState<string[]>([]);
  const [selectedTaxonomyTags, setSelectedTaxonomyTags] =
    useState<ITaxonomyTags>({});

  const [
    fetchSearchResults,
    { data: searchResponse, loading: isSearchLoading },
  ] = useFetchSearchResults({
    searchParams,
    searchId,
    canModifyContentSettings,
    sendAdminContentSettings,
    selectedTaxonomyTags,
  });

  const [fetchReadingSkills, { data: readingSkills }] =
    useLazyReadingSkillsQuery() as [
      () => void,
      { data: IReadingSkill[] | undefined; loading: boolean; error: Error },
    ];

  const [
    fetchTaxonomyData,
    { data: taxonomyData, loading: isLoadingTaxonomyData },
  ] = useTaxonomyTagsFetch(TAXONOMY_DEPTH) as [
    () => void,
    { data: ITaxonomyApiResponse | undefined; loading: boolean; error: Error },
  ];

  const {
    getAllSearchParams,
    setSearchParamValue,
    hasSearchParamValue,
    getSearchParamValues,
    toggleSearchParamValue,
    deleteSearchParamValues,
  } = useSearchParamsControls({ searchParams, setSearchParams });

  // Fetches reading skills and taxonomy once
  useEffect(() => {
    fetchReadingSkills();
    fetchTaxonomyData();
  }, []);

  // Debounces all changes to searchParams
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedParams(searchParams.toString());
    }, 1000);
    return () => {
      clearTimeout(handler);
    };
  }, [searchParams.toString()]);

  // Updates to the searchParams via the custom functions from useSearchParamsControls()
  // will trigger this useEffect: a new search request, and the search params to be pushed to the URL.
  useEffect(() => {
    fetchSearchResults();
    void push({
      pathname: "/search",
      search: `?${searchParams.toString()}`,
    });
  }, [debouncedParams, sendAdminContentSettings, selectedTaxonomyTags]);

  // Detects if the search params were changed from outside the context (ex: navbar search)
  // causing the useEffect above to trigger.
  useEffect(() => {
    setSearchParams(initializeSearchParams(query));
  }, [stringify(query)]);

  useEffect(() => {
    if (searchResponse) {
      setSearchId(searchResponse.search_id);
      const [currentPage] = getSearchParamValues(AgathaSchemaParamsEnum.page);
      if (Number(currentPage) === 0) {
        setSearchResults(searchResponse.results);
      } else {
        setSearchResults([...searchResults, ...searchResponse.results]);
      }
    }
  }, [searchResponse]);

  const agathaSchema = useFetchAgathaSchema();
  if (!agathaSchema) return null;
  // Agatha schema params are saved here
  const validQueryParams = getValidQueryParams(agathaSchema);

  return (
    <SearchPageContext.Provider
      value={{
        getters: {
          readingSkills,
          searchResponse,
          searchResults,
          isSearchLoading,
          validQueryParams,
          sendAdminContentSettings,
          taxonomyResults: taxonomyData?.results || [],
          isLoadingTaxonomyData,
          searchParams,
          selectedTaxonomyTags,
          taxonomyTagsSlugs,
        },
        handlers: {
          getAllSearchParams,
          setSearchParamValue,
          hasSearchParamValue,
          getSearchParamValues,
          toggleSearchParamValue,
          deleteSearchParamValues,
          setSendAdminContentSettings,
          setSelectedTaxonomyTags,
          setTaxonomyTagsSlugs,
        },
      }}
    >
      {children}
    </SearchPageContext.Provider>
  );
};

export interface ISearchPageContext {
  getters: {
    isSearchLoading: boolean;
    readingSkills: IReadingSkill[] | undefined;
    searchResponse: ISearchResultsResponse | undefined;
    searchResults: ISearchResult[];
    validQueryParams: { [key: string]: IAgathaSearchParams };
    sendAdminContentSettings: boolean;
    taxonomyResults: ITaxonomyApiResponseContent[];
    isLoadingTaxonomyData: boolean;
    searchParams: URLSearchParams;
    selectedTaxonomyTags: ITaxonomyTags;
    taxonomyTagsSlugs: string[];
  };
  handlers: {
    getAllSearchParams: () => Array<[paramName: string, paramValue: string]>;
    getSearchParamValues: (name: string) => string[];
    setSearchParamValue: (paramName: string, paramValue: string) => void;
    hasSearchParamValue: (paramName: string, paramValue: string) => boolean;
    toggleSearchParamValue: (paramName: string, paramValue: string) => void;
    deleteSearchParamValues: (paramName: string) => void;
    setSendAdminContentSettings: (sendAdminContentSettings: boolean) => void;
    setSelectedTaxonomyTags: (taxonomyTags: ITaxonomyTags) => void;
    setTaxonomyTagsSlugs: (taxonomyTagsSlugs: string[]) => void;
  };
}

interface ISearchPageProvider {
  children: ReactElement;
}
SearchPageProvider.displayName = "SearchPageProvider";
export { SearchPageProvider };
